import { useCallback, useEffect, useRef, useState } from "react";
import { mappingApi } from "@services/mappingApi";
import { ApiError } from "@utils/httpClient";
import { TERMINAL_STATUSES } from "@utils/mappingServiceHelpers";
import type {
  JobListFilterViewModel,
  JobViewModel,
} from "@models/job";

interface ParsedService {
  kind: string;
  pluginId: string;
}

interface UseJobHistoryArgs {
  parsed: ParsedService | null;
  filter: JobListFilterViewModel;
  subscribe: (jobId: string) => void;
}

export interface UseJobHistoryResult {
  jobs: JobViewModel[];
  hasMore: boolean;
  isLoadingInitial: boolean;
  isLoadingMore: boolean;
  loadedOnce: boolean;
  error: string | null;
  clearError: () => void;
  upsertJob: (job: JobViewModel) => void;
  loadMore: () => void;
  reload: () => void;
  deleteJob: (jobId: string) => Promise<boolean>;
}

const PAGE_SIZE = 25;
const POLL_INTERVAL_MS = 5_000;

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof ApiError
    ? err.message
    : err instanceof Error
      ? err.message
      : fallback;

export const useJobHistory = ({
  parsed,
  filter,
  subscribe,
}: UseJobHistoryArgs): UseJobHistoryResult => {
  const [jobs, setJobs] = useState<JobViewModel[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);

  const upsertJob = useCallback((next: JobViewModel): void => {
    setJobs((prev) => {
      const idx = prev.findIndex((j) => j.jobId === next.jobId);
      if (idx === -1) return [next, ...prev];
      const updated = [...prev];
      updated[idx] = { ...prev[idx], ...next };
      return updated;
    });
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      for (const job of jobs) {
        if (TERMINAL_STATUSES.has(job.status)) continue;
        void mappingApi.getJob(job.jobId).then(upsertJob).catch(() => {});
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [jobs, upsertJob]);

  const fetchPage = useCallback(
    async (cursorArg: string | null, replace: boolean): Promise<void> => {
      if (!parsed) return;
      if (replace) setIsLoadingInitial(true);
      else setIsLoadingMore(true);
      setError(null);
      try {
        const response = await mappingApi.listJobs({
          service: parsed.kind,
          pluginId: parsed.pluginId,
          cursor: cursorArg ?? undefined,
          limit: PAGE_SIZE,
          status: filter.status === "all" ? undefined : filter.status,
          userId: filter.userId,
          startDate: filter.startDate,
          endDate: filter.endDate,
        });
        const fetched = response.jobs;
        setJobs((prev) => {
          if (replace) return fetched;
          const seen = new Set(prev.map((j) => j.jobId));
          return [...prev, ...fetched.filter((j) => !seen.has(j.jobId))];
        });
        setCursor(response.cursor);
        for (const job of fetched) subscribe(job.jobId);
      } catch (err) {
        setError(errorMessage(err, "Failed to load job history"));
      } finally {
        setLoadedOnce(true);
        if (replace) setIsLoadingInitial(false);
        else setIsLoadingMore(false);
      }
    },
    [parsed, filter, subscribe],
  );

  const fetchPageRef = useRef(fetchPage);
  useEffect(() => {
    fetchPageRef.current = fetchPage;
  }, [fetchPage]);

  useEffect(() => {
    if (!parsed) return;
    setJobs([]);
    setCursor(null);
    setLoadedOnce(false);
    void fetchPageRef.current(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed?.kind, parsed?.pluginId, filter]);

  const loadMore = useCallback((): void => {
    if (cursor && !isLoadingMore) void fetchPage(cursor, false);
  }, [cursor, isLoadingMore, fetchPage]);

  const reload = useCallback((): void => {
    void fetchPage(null, true);
  }, [fetchPage]);

  const deleteJob = useCallback(
    async (jobId: string): Promise<boolean> => {
      try {
        await mappingApi.deleteJob(jobId);
        setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
        return true;
      } catch (err) {
        setError(errorMessage(err, "Failed to delete job"));
        return false;
      }
    },
    [],
  );

  return {
    jobs,
    hasMore: cursor !== null,
    isLoadingInitial,
    isLoadingMore,
    loadedOnce,
    error,
    clearError,
    upsertJob,
    loadMore,
    reload,
    deleteJob,
  };
};
